import { CharacterClass } from '../CharacterTypes';
import { TALENT_TREES } from '../classes/TalentTrees';
import type { TalentNode, TalentTree } from '../types/Progression';

export const TALENT_POINTS_PER_LEVEL = 3;

export type TalentRanks = Record<string, number>;

export interface TalentState {
  earnedPoints: number;
  spentPoints: number;
  availablePoints: number;
  ranks: TalentRanks;
}

export interface TalentStatBonuses {
  strength: number;
  dexterity: number;
  intelligence: number;
  hp: number;
  magicAttack: number;
  defense: number;
  magicDefense: number;
  criticalRate: number;
}

export type TalentSpendRejection =
  | 'tree_not_found'
  | 'node_not_found'
  | 'prerequisite_locked'
  | 'max_rank'
  | 'not_enough_points';

export type TalentSpendDecision =
  | { ok: true; state: TalentState; node: TalentNode }
  | { ok: false; reason: TalentSpendRejection; state: TalentState };

function treeForClass(className: string): TalentTree | null {
  if (typeof className !== 'string') return null;
  const normalized = className.toLowerCase() as CharacterClass;
  return Object.values(CharacterClass).includes(normalized)
    ? TALENT_TREES[normalized] ?? null
    : null;
}

function allNodes(tree: TalentTree | null): TalentNode[] {
  return tree ? Object.values(tree).flat() : [];
}

/** Talent points are level-derived authority, so an omitted or stale stored
 * point pool can never create or destroy spendable points on reconnect. */
export function earnedTalentPoints(level: number): number {
  const safeLevel = Number.isFinite(level) ? Math.max(1, Math.floor(level)) : 1;
  return (safeLevel - 1) * TALENT_POINTS_PER_LEVEL;
}

/** Keep only nodes authored for this class, positive integer ranks, and clamp
 * legacy/corrupt values to their authored maximum. */
export function normalizeTalentRanks(className: string, value: unknown): TalentRanks {
  const nodes = allNodes(treeForClass(className));
  if (!value || typeof value !== 'object') return {};

  const source = value instanceof Map
    ? Object.fromEntries(value.entries())
    : value as Record<string, unknown>;
  const normalized: TalentRanks = {};
  for (const node of nodes) {
    const raw = source[node.id];
    if (!Number.isInteger(raw) || (raw as number) <= 0) continue;
    normalized[node.id] = Math.min(raw as number, node.maxRank);
  }
  return normalized;
}

/** Rebuild a rank set through the authored order and the level-derived point
 * budget. This fail-closed pass prevents malformed legacy/storage input from
 * granting effects that a player could not have allocated through the spend
 * protocol. */
export function authorizedTalentRanks(
  className: string,
  level: number,
  value: unknown,
): TalentRanks {
  const tree = treeForClass(className);
  const requested = normalizeTalentRanks(className, value);
  const authorized: TalentRanks = {};
  let remaining = earnedTalentPoints(level);

  for (const node of allNodes(tree)) {
    const requestedRank = requested[node.id] ?? 0;
    if (node.prerequisites.some(prerequisite => (authorized[prerequisite] ?? 0) < 1)) {
      continue;
    }
    const affordable = Math.floor(remaining / node.cost);
    const rank = Math.min(requestedRank, affordable);
    if (rank <= 0) continue;
    authorized[node.id] = rank;
    remaining -= rank * node.cost;
  }
  return authorized;
}

export function talentStateFor(
  className: string,
  level: number,
  value: unknown,
): TalentState {
  const tree = treeForClass(className);
  const ranks = authorizedTalentRanks(className, level, value);
  const byId = new Map(allNodes(tree).map(node => [node.id, node]));
  const spentPoints = Object.entries(ranks).reduce((sum, [nodeId, rank]) => {
    return sum + (byId.get(nodeId)?.cost ?? 0) * rank;
  }, 0);
  const earnedPoints = earnedTalentPoints(level);
  return {
    earnedPoints,
    spentPoints,
    availablePoints: Math.max(0, earnedPoints - spentPoints),
    ranks,
  };
}

export function resolveTalentSpend(
  className: string,
  level: number,
  value: unknown,
  treeName: string,
  nodeId: string,
): TalentSpendDecision {
  const tree = treeForClass(className);
  const state = talentStateFor(className, level, value);
  const branch = tree?.[treeName];
  if (!branch) return { ok: false, reason: 'tree_not_found', state };
  const node = branch.find(candidate => candidate.id === nodeId);
  if (!node) return { ok: false, reason: 'node_not_found', state };

  const currentRank = state.ranks[node.id] ?? 0;
  if (currentRank >= node.maxRank) return { ok: false, reason: 'max_rank', state };
  if (node.prerequisites.some(prerequisite => (state.ranks[prerequisite] ?? 0) < 1)) {
    return { ok: false, reason: 'prerequisite_locked', state };
  }
  if (state.availablePoints < node.cost) {
    return { ok: false, reason: 'not_enough_points', state };
  }

  const ranks = { ...state.ranks, [node.id]: currentRank + 1 };
  return {
    ok: true,
    node,
    state: talentStateFor(className, level, ranks),
  };
}

/** Translate the authored prototype vocabulary into the live combat-stat
 * vocabulary. Primary-stat aliases feed the normal derived-stat calculation;
 * flat bonuses are added after equipment and auras by EquipmentUtils. */
export function talentStatBonusesFor(
  className: string,
  level: number,
  value: unknown,
): TalentStatBonuses {
  const ranks = authorizedTalentRanks(className, level, value);
  const bonuses: TalentStatBonuses = {
    strength: 0,
    dexterity: 0,
    intelligence: 0,
    hp: 0,
    magicAttack: 0,
    defense: 0,
    magicDefense: 0,
    criticalRate: 0,
  };

  for (const node of allNodes(treeForClass(className))) {
    const rank = ranks[node.id] ?? 0;
    if (rank === 0) continue;
    for (const effect of node.effects) {
      if (effect.type !== 'stat_bonus') continue;
      const total = effect.value * (effect.perRank === false ? 1 : rank);
      switch (effect.target) {
      case 'strength': bonuses.strength += total; break;
      case 'agility': bonuses.dexterity += total; break;
      case 'intelligence': bonuses.intelligence += total; break;
      case 'wisdom': bonuses.intelligence += total; break;
      // The runtime has no standalone endurance stat. Two authored endurance
      // points therefore become twenty max-health points.
      case 'endurance': bonuses.hp += total * 10; break;
      case 'maxHp': bonuses.hp += total; break;
      case 'magicAttack': bonuses.magicAttack += total; break;
      case 'defense': bonuses.defense += total; break;
      case 'magicDefense': bonuses.magicDefense += total; break;
      // Authored values are fractions (0.01 = one percentage point), while
      // Player.criticalRate is expressed in percentage points.
      case 'criticalChance': bonuses.criticalRate += total * 100; break;
      }
    }
  }
  return bonuses;
}

export function projectTalentTree(className: string, value: unknown): TalentTree {
  const tree = treeForClass(className);
  if (!tree) return {};
  const ranks = normalizeTalentRanks(className, value);
  return Object.fromEntries(Object.entries(tree).map(([treeName, nodes]) => [
    treeName,
    nodes.map(node => ({ ...node, currentRank: ranks[node.id] ?? 0 })),
  ]));
}
